import type { Metadata } from "@/types/metadata";

// Import all layout modules to extract metadata
const layouts = import.meta.glob("/src/app/**/layout.tsx", { eager: false });

/**
 * Extract metadata from a layout module
 */
async function extractMetadataFromLayout(layoutPath: string): Promise<Metadata | null> {
  try {
    const layoutModule = await layouts[layoutPath]();
    const module = layoutModule as {
      generateMetadata?: () => Promise<Metadata> | Metadata;
      default?: React.ComponentType<any>;
    };

    if (module.generateMetadata) {
      const metadataResult = module.generateMetadata();
      // Handle both sync and async metadata
      const metadata = metadataResult instanceof Promise ? await metadataResult : metadataResult;

      // Ensure metadata has the correct format
      if (metadata && typeof metadata === "object") {
        return metadata as Metadata;
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Failed to extract metadata from ${layoutPath}:`, error);
  }

  return null;
}

/**
 * Find all layouts for a route based on the file path (from root to closest)
 * Uses the same logic as generateRoutes.tsx
 */
function findLayoutsForRoute(filePath: string): string[] {
  // Extract directory path from file path
  const dirPath = filePath.replace(/\/page\.tsx$/, "");

  // Build all possible layout paths from root to closest
  const allPaths: string[] = [];

  // Start from root and build up to the page directory
  allPaths.push("/src/app/layout.tsx");

  // Build paths from root to page directory
  const pathParts = dirPath
    .replace(/^\/src\/app/, "")
    .split("/")
    .filter(Boolean);

  let currentPath = "/src/app";
  for (const part of pathParts) {
    currentPath = `${currentPath}/${part}`;
    allPaths.push(`${currentPath}/layout.tsx`);
  }

  // Find all existing layouts (from root to closest)
  const foundLayouts: string[] = [];
  for (const testPath of allPaths) {
    if (layouts[testPath]) {
      foundLayouts.push(testPath);
    }
  }

  return foundLayouts;
}

/**
 * Convert route path to file path
 * Handles dynamic routes and route groups
 */
function routePathToFilePath(routePath: string): string | null {
  const pages = import.meta.glob("/src/app/**/page.tsx", { eager: false });

  // Convert route path to route path format (same as generateRoutes)
  function filePathToRoutePath(filePath: string): string {
    let routePath = filePath.replace(/^\/src\/app/, "");
    routePath = routePath.replace(/\/page\.tsx$/, "");
    routePath = routePath.replace(/\([^)]+\)/g, "");
    if (routePath === "" || routePath === "/") {
      return "/";
    }
    routePath = routePath.replace(/\[([^\]]+)\]/g, ":$1");
    routePath = routePath.replace(/\[\.\.\.([^\]]+)\]/g, "*");
    routePath = routePath.replace(/\/+/g, "/");
    if (!routePath.startsWith("/")) {
      routePath = "/" + routePath;
    }
    return routePath;
  }

  // Find matching page file
  for (const filePath of Object.keys(pages)) {
    const pageRoutePath = filePathToRoutePath(filePath);
    if (pageRoutePath === routePath) {
      return filePath;
    }
  }

  return null;
}

/**
 * Get metadata for a specific route
 * Collects metadata from all layouts in the hierarchy and merges them
 */
export async function getMetadataForRoute(routePath: string): Promise<Metadata | null> {
  // Find the page file for this route
  const pageFilePath = routePathToFilePath(routePath);

  if (!pageFilePath) {
    // No matching page found, return null
    return null;
  }

  // Get layout paths for the matching page (same logic as generateRoutes)
  const layoutPaths = findLayoutsForRoute(pageFilePath);

  // Extract metadata from all layouts
  const metadataResults = await Promise.all(layoutPaths.map((path) => extractMetadataFromLayout(path)));

  // Merge metadata (closest layout overrides parent)
  // In Next.js, metadata is merged with closest taking precedence
  const mergedMetadata = metadataResults
    .filter((m): m is Metadata => m !== null)
    .reduce((acc, metadata) => {
      // Merge with closest layout overriding parent
      return {
        ...acc,
        ...metadata,
        // Deep merge openGraph
        openGraph: {
          ...acc.openGraph,
          ...metadata.openGraph,
        },
      };
    }, {} as Metadata);

  // Cache the result
  if (Object.keys(mergedMetadata).length > 0) {
    setCachedMetadataForRoute(routePath, mergedMetadata);
    return mergedMetadata;
  }

  return null;
}

/**
 * Synchronous version that uses cached metadata
 * For use in components that need immediate metadata
 */
const metadataCache = new Map<string, Metadata>();

export function getCachedMetadataForRoute(routePath: string): Metadata | null {
  return metadataCache.get(routePath) || null;
}

export function setCachedMetadataForRoute(routePath: string, metadata: Metadata): void {
  metadataCache.set(routePath, metadata);
}
